import { InputMask } from '@react-input/mask'
import { useFormContext } from 'react-hook-form'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'

import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { primaryIbanOptions } from '@/constants'
import { useAppSelector } from '@/hooks'
import { capitalizeFirstLetter } from '@/lib/helper'
import { BookShelfForm } from '@/pages/customer/book-shelf'
import bookingService from '@/services/booking-service'
import { format } from 'date-fns'

interface ContactFormProps {
	couponDetail: Coupon | null
	referralDetail: Referral | null
	setCouponDetail: (value: Coupon | null) => void
	setReferralDetail: (value: Referral | null) => void
}
export const ContactForm = ({
	couponDetail,
	referralDetail,
	setCouponDetail,
	setReferralDetail,
}: ContactFormProps) => {
	const auth = useAppSelector(state => state.auth)
	const form = useFormContext<BookShelfForm>()

	const { mutate: verifyCouponCode } = useMutation({
		mutationKey: 'verify-coupon-code',
		mutationFn: (startDate: string) =>
			bookingService.verifyCouponCode(
				form.watch('couponCode') as string,
				startDate,
				Number(form.watch('package')),
				auth?.id,
			),
		onSuccess: res => {
			if (!res.status) {
				if (Number(form.watch('package')) === 5) {
					toast.error(res.message || 'Der Code ist nur gültig für Buchungen mit Mietzahlung.')
				} else {
					toast.error('Der Gutschein ist nicht gültig')
				}
				setCouponDetail(null)
			} else if (
				(res.message === 'This is a bonus coupon' && Number(form.watch('package')) < 2) ||
				Number(form.watch('package')) > 4
			) {
				toast.error(
					'Der von dir eingegebene Code kann nur für Buchungen ab 2 Wochen eingelöst werden.',
				)
				setCouponDetail(null)
			} else if (res.data) {
				toast.success('Gutschein erfolgreich angewendet!')
				setCouponDetail(res.data)
			}
		},
		onError: () => {
			toast.error('Der Gutschein ist nicht gültig')
			setCouponDetail(null)
		},
	})

	const { mutate: verifyReferralCode } = useMutation({
		mutationKey: 'verify-referral-code',
		mutationFn: () =>
			bookingService.verifyReferralCode(
				form.watch('referralCode') as string,
				form.watch('email') as string,
			),
		onSuccess: res => {
			if (!res.status) {
				toast.error('Der Code ist ungültig')
				setReferralDetail(null)
			}
			if (res.data) {
				setReferralDetail(res.data)
			}
		},
		onError: () => {
			toast.error('Der Code ist ungültig')
			setReferralDetail(null)
		},
	})

	return (
		<div className="mt-10 lg:mt-12 flex flex-col">
			<h5 className="text-secondary/50 max-lg:text-xs font-medium">
				Bitte geben Sie Ihre Kontaktdaten ein
			</h5>
			<div className="grid grid-cols-1 items-start lg:grid-cols-2 mt-8 lg:mt-12 lg:gap-x-5 gap-y-6 lg:gap-y-8">
				<FormField
					control={form.control}
					name="firstName"
					render={({ field }) => (
						<FormItem className="w-full">
							<FormLabel className="text-muted-foreground">Name</FormLabel>
							<FormControl>
								<Input placeholder="Name" {...field} value={capitalizeFirstLetter(field.value)} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="lastName"
					render={({ field }) => (
						<FormItem className="w-full">
							<FormLabel className="text-muted-foreground">Nachname</FormLabel>
							<FormControl>
								<Input
									placeholder="Nachname"
									{...field}
									value={capitalizeFirstLetter(field.value)}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="address"
					render={({ field }) => (
						<FormItem className="w-full lg:col-span-2">
							<FormLabel className="text-muted-foreground">Adresse</FormLabel>
							<FormControl>
								<Input
									placeholder="Adresse"
									{...field}
									value={capitalizeFirstLetter(field.value)}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="country"
					render={({ field }) => (
						<FormItem className="w-full">
							<FormLabel className="text-muted-foreground">Ort</FormLabel>
							<FormControl>
								<Input placeholder="Ort" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="zip"
					render={({ field }) => (
						<FormItem className="w-full">
							<FormLabel className="text-muted-foreground">Postleitzahl</FormLabel>
							<FormControl>
								<Input placeholder="Postleitzahl" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
			<div className="grid grid-cols-1 lg:grid-cols-3 items-start mt-6 lg:mt-8 max-lg:gap-y-6 lg:gap-x-5">
				<FormField
					control={form.control}
					name="phoneNumber"
					render={({ field }) => (
						<FormItem className="w-full">
							<FormLabel className="text-muted-foreground">Telefonnummer</FormLabel>
							<FormControl>
								<Input placeholder="Telefonnummer" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem className="w-full">
							<FormLabel className="text-muted-foreground">E-Mail</FormLabel>
							<FormControl>
								<Input placeholder="E-mail" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="iban"
					render={({ field }) => (
						<FormItem className="w-full">
							<FormLabel className="text-muted-foreground">
								IBAN <span className="text-destructive-foreground">*</span>
							</FormLabel>
							<FormControl>
								<InputMask
									{...primaryIbanOptions}
									value={field.value ?? ''}
									onChange={e => field.onChange(e.target.value)}
									placeholder="IBAN"
									component={Input}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
			<div className="grid grid-cols-1 items-start lg:grid-cols-2 mt-6 lg:mt-8 gap-y-6 lg:gap-x-[46px]">
				<div className="flex items-center gap-2 lg:gap-3">
					<div className="relative">
						<FormField
							control={form.control}
							name="couponCode"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel className="text-muted-foreground">Gutschein</FormLabel>
									<FormControl>
										<Input disabled={!!referralDetail} placeholder="Gutschein" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{couponDetail && (
							<Badge
								className="absolute mt-2 cursor-pointer"
								onClick={() => {
									setCouponDetail(null)
									form.setValue('couponCode', '')
								}}
								variant="destructive">
								Klarer Gutschein
							</Badge>
						)}
					</div>
					<Button
						type="button"
						disabled={!form.watch('couponCode')}
						onClick={() => {
							const formatLocalDate = (date: Date) => {
								const year = date.getFullYear()
								const month = `${date.getMonth() + 1}`.padStart(2, '0')
								const day = `${date.getDate()}`.padStart(2, '0')
								return `${year}-${month}-${day}`
							}

							const startDate = formatLocalDate(
								new Date(format(form.getValues('startDate'), 'yyyy-MM-dd')),
							)
							verifyCouponCode(startDate)
						}}
						variant="secondary"
						className="self-end h-10 lg:h-14 rounded-[12px]">
						VERIFIZIEREN
					</Button>
				</div>
				<div className="flex items-center gap-2 lg:gap-3">
					<div>
						<FormField
							control={form.control}
							name="referralCode"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel className="text-muted-foreground">Empfehlungs-Code</FormLabel>
									<FormControl>
										<Input disabled={!!couponDetail} placeholder="Empfehlungs-Code" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{referralDetail && (
							<Badge
								className="absolute mt-2 cursor-pointer"
								onClick={() => {
									setReferralDetail(null)
									form.setValue('referralCode', '')
								}}
								variant="destructive">
								Klare Empfehlung
							</Badge>
						)}
					</div>
					<Button
						type="button"
						onClick={() => {
							if (Number(form.watch('package')) < 2 || Number(form.watch('package')) > 4) {
								toast.error(
									'Der von dir eingegebene Code kann nur für Buchungen ab 2 Wochen eingelöst werden.',
								)
							} else {
								verifyReferralCode()
							}
						}}
						disabled={!form.watch('referralCode')}
						variant="secondary"
						className="self-end h-10 lg:h-14 rounded-[12px]">
						VERIFIZIEREN
					</Button>
				</div>
			</div>
			<div className="mt-6 lg:mt-10">
				<p className="text-secondary max-lg:text-xs font-medium">
					*Wir benötigen deine <span className="font-extrabold">IBAN</span> für die Auszahlung
					deines Guthabens am Ende deiner Mietlaufzeit.
				</p>
			</div>
			<div className="mt-6 lg:mt-12 flex flex-col gap-6 lg:gap-[26px]">
				<FormField
					control={form.control}
					name="agreeTerms"
					render={({ field }) => (
						<FormItem className="w-full">
							<div className="flex flex-row items-start gap-1 lg:gap-2">
								<FormControl>
									<Checkbox
										checked={field.value}
										onCheckedChange={field.onChange}
										className="border-secondary/50"
									/>
								</FormControl>
								<FormLabel className="text-secondary max-lg:text-xs font-medium">
									Ich stimme den{' '}
									<Link to="/letter" target="__blank" className="text-[#68A8F5] underline">
										Allgemeinen Geschäftsbedingungen
									</Link>{' '}
									von minmini zu.
								</FormLabel>
							</div>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="privacyPolicy"
					render={({ field }) => (
						<FormItem className="w-full">
							<div className="flex flex-row items-start gap-1 lg:gap-2">
								<FormControl>
									<Checkbox
										checked={field.value}
										onCheckedChange={field.onChange}
										className="border-secondary/50"
									/>
								</FormControl>
								<FormLabel className="text-secondary max-lg:text-xs font-medium">
									Ich habe die{' '}
									<Link to="/datenschutz" target="__blank" className="text-[#68A8F5] underline">
										Datenschutzinformationen
									</Link>{' '}
									von minmini zur Kenntnis genommen. Ein Widerruf der Zustimmung ist jederzeit
									möglich. von minmini zu.
								</FormLabel>
							</div>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="receiveNews"
					render={({ field }) => (
						<FormItem className="flex flex-row items-start gap-1 lg:gap-2">
							<FormControl>
								<Checkbox
									checked={field.value}
									onCheckedChange={field.onChange}
									className="border-secondary/50"
								/>
							</FormControl>
							<FormLabel className="text-secondary max-lg:text-xs font-medium">
								Ja, ich möchte Neuigkeiten über Angebote und Updates von minmini erhalten. Ein
								Widerruf der Einwilligung ist jederzeit möglich.
							</FormLabel>
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="cancellationPolicy"
					render={({ field }) => (
						<FormItem className="w-full">
							<div className="flex flex-row items-start gap-1 lg:gap-2">
								<FormControl>
									<Checkbox
										checked={field.value}
										onCheckedChange={field.onChange}
										className="border-secondary/50"
									/>
								</FormControl>
								<FormLabel className="text-secondary max-lg:text-xs font-medium">
									Ich akzeptiere die{' '}
									<Link to="/widerruf" target="__blank" className="text-[#68A8F5] underline">
										Widerrufsbelehrung.
									</Link>
								</FormLabel>
							</div>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
		</div>
	)
}
